import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const DentistNearWestoverHills = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental",
    "description": "Best dentist near Westover Hills, Fort Worth. Board-certified implant dentist, LANAP laser treatment, Invisalign, emergency dentistry.",
    "url": "https://fortworthdental.com",
    "telephone": "+1-817-985-3337",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "areaServed": ["Westover Hills", "Fort Worth", "Benbrook", "Aledo", "Ridglea"],
    "priceRange": "$$"
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dentist Near Westover Hills | Fort Worth TX | Fort Worth Dental</title>
        <meta name="description" content="Looking for a dentist near Westover Hills? Fort Worth Dental is your neighborhood dental practice. Dental implants, Invisalign, LANAP. Call (817) 985-3337." />
        <meta name="keywords" content="dentist westover hills, dentist near westover hills fort worth, best dentist westover hills tx, dental implants westover hills" />
        <link rel="canonical" href="https://fortworthdental.com/dentist-near-westover-hills" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-teal-200 font-semibold mb-2">Your Neighborhood Dental Practice</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Dentist Near Westover Hills, Fort Worth</h1>
            <p className="text-xl mb-8">Fort Worth Dental proudly serves the Westover Hills community. Experience personalized dental care with our award-winning team, just minutes from your home.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:8179853337" className="bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100">Call (817) 985-3337</a>
              <Link to="/contact-us" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10">Schedule Online</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Focus */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Serving Westover Hills Since 1990</h2>
            <p className="text-gray-600 text-lg mb-8">For over 35 years, Fort Worth Dental has been the trusted dental home for Westover Hills families. Our convenient Citylake location is just a short drive from the Westover Hills neighborhood, making it easy to fit dental care into your busy schedule.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-teal-600 mb-2">35+</div>
                <p className="text-gray-600">Years Serving the Community</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-teal-600 mb-2">500+</div>
                <p className="text-gray-600">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-teal-600 mb-2">5</div>
                <p className="text-gray-600">Minutes from Westover Hills</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Complete Dental Services for Westover Hills</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Dental Implants', desc: 'Board-certified implant dentist with 35+ years experience', link: '/dental-implants', icon: '🦷' },
              { title: 'Invisalign', desc: 'Clear aligners for a straighter smile', link: '/invisalign', icon: '😁' },
              { title: 'LANAP Laser', desc: 'Advanced gum disease treatment', link: '/lanap', icon: '⚡' },
              { title: 'Emergency Care', desc: 'Same-day appointments available', link: '/emergency-dentist', icon: '🚨' },
              { title: 'Cosmetic Dentistry', desc: 'Veneers, whitening, smile makeovers', link: '/cosmetic-dentistry', icon: '✨' },
              { title: 'Family Dentistry', desc: 'Care for patients of all ages', link: '/preventative-dentistry', icon: '👨‍👩‍👧' },
            ].map((service, i) => (
              <Link key={i} to={service.link} className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition group">
                <div className="text-3xl mb-3">{service.icon}</div>
                <h3 className="font-bold text-gray-800 mb-2 group-hover:text-teal-600">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Directions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Easy Access from Westover Hills</h2>
              <p className="text-gray-600 mb-6">Located on Citylake Blvd W, Fort Worth Dental is conveniently positioned to serve Westover Hills residents. We're just minutes away via Camp Bowie or I-30.</p>
              <div className="bg-teal-50 p-6 rounded-lg">
                <h3 className="font-bold text-teal-800 mb-4">Fort Worth Dental</h3>
                <p className="text-gray-700">4620 Citylake Blvd W</p>
                <p className="text-gray-700">Fort Worth, TX 76132</p>
                <p className="text-teal-600 font-bold text-lg mt-4">(817) 985-3337</p>
                <div className="mt-4 pt-4 border-t border-teal-200">
                  <p className="text-sm text-teal-700"><strong>Hours:</strong></p>
                  <p className="text-sm text-gray-600">Mon-Thu: 8am-5pm</p>
                  <p className="text-sm text-gray-600">Fri: 8am-2pm</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-200 rounded-lg overflow-hidden h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3357.0!2d-97.438!3d32.728!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQzJzQxLjAiTiA5N8KwMjYnMTcuNCJX!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Fort Worth Dental - Near Westover Hills"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Your Westover Hills Neighborhood Dentist</h2>
          <p className="text-xl mb-8">Experience the difference at Fort Worth Dental. Schedule your appointment today!</p>
          <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 inline-block">Call (817) 985-3337</a>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Book Your Appointment</h2>
          <AppointmentForm source="westover_hills_landing" />
        </div>
      </section>
    </div>
  );
};

export default DentistNearWestoverHills;